// Community data types and mock data for LÍDER IA

export type ThreadCategory = 
  | 'questions' 
  | 'projects' 
  | 'collaborations' 
  | 'ideas'
  | 'contributions';

export type ThreadTag = 
  | 'automation' 
  | 'ai-assistants' 
  | 'data-analysis' 
  | 'workshop' 
  | 'blocker' 
  | 'real-case';

export type ThreadStatus = 'open' | 'closed';

export interface ThreadAuthor {
  id: string;
  name: string;
  avatar: string;
  company: string;
}

export interface ForumThread {
  id: string;
  title: string;
  description: string;
  category: ThreadCategory;
  tags: ThreadTag[];
  author: ThreadAuthor;
  projectId?: string;
  workshopId?: string;
  externalLink?: string;
  status: ThreadStatus;
  createdAt: string;
  lastActivity: string;
  responseCount: number;
}

export interface ThreadResponse {
  id: string;
  threadId: string;
  content: string;
  author: ThreadAuthor;
  isSolution: boolean;
  createdAt: string;
}

export const THREAD_CATEGORIES: Record<ThreadCategory, { label: string; color: string; icon: string }> = {
  questions: { label: 'Preguntas', color: 'bg-amber-500/10 text-amber-600 border-amber-200', icon: '❓' },
  projects: { label: 'Proyectos', color: 'bg-purple-500/10 text-purple-600 border-purple-200', icon: '🧪' },
  collaborations: { label: 'Colaboraciones', color: 'bg-green-500/10 text-green-600 border-green-200', icon: '🤝' },
  contributions: { label: 'Aporte a la Comunidad', color: 'bg-pink-500/10 text-pink-600 border-pink-200', icon: '🎁' },
  ideas: { label: 'Ideas', color: 'bg-cyan-500/10 text-cyan-600 border-cyan-200', icon: '💡' },
};

export const THREAD_TAGS: Record<ThreadTag, { label: string; color: string }> = {
  'automation': { label: 'Automatización', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  'ai-assistants': { label: 'Asistentes IA', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  'data-analysis': { label: 'Análisis de Datos', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  'workshop': { label: 'Sesión en Vivo', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  'blocker': { label: 'Bloqueo', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  'real-case': { label: 'Caso real', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
};

export const mockAuthors: ThreadAuthor[] = [
  { id: 'p1', name: 'María García', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Maria', company: 'TechSolutions MX' },
  { id: 'p2', name: 'Carlos López', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Carlos', company: 'DataDriven Co' },
  { id: 'p3', name: 'Ana Martínez', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Ana', company: 'InnovateMX' },
  { id: 'm1', name: 'Dr. Roberto Sánchez', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Roberto', company: 'LÍDER IA' },
  { id: 'm2', name: 'Ing. Laura Fernández', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Laura', company: 'LÍDER IA' },
];

export const mockThreads: ForumThread[] = [
  {
    id: 'thread-1',
    title: 'Error al conectar Make con Google Sheets - Timeout después de 30 segundos',
    description: 'Estoy intentando crear un escenario en Make que actualice automáticamente una hoja de Google Sheets cada vez que recibo un email, pero me da un error de timeout. Ya revisé los permisos y todo parece estar bien. ¿Alguien ha tenido este problema?',
    category: 'questions',
    tags: ['automation', 'blocker'],
    author: mockAuthors[0],
    projectId: 'proj-1',
    status: 'open',
    createdAt: '2025-12-20T14:30:00Z',
    lastActivity: '2025-12-21T09:15:00Z',
    responseCount: 4,
  },
  {
    id: 'thread-2',
    title: '¡Nueva sesión en vivo disponible! Automatización avanzada con n8n',
    description: 'Nos complace anunciar la próxima sesión en vivo de automatización avanzada donde exploraremos flujos complejos con n8n. Fecha: 5 de Enero. ¡No te lo pierdas!',
    category: 'projects',
    tags: ['workshop', 'automation'],
    author: mockAuthors[3],
    workshopId: 'evt7',
    status: 'open',
    createdAt: '2025-12-19T10:00:00Z',
    lastActivity: '2025-12-19T10:00:00Z',
    responseCount: 12,
  },
  {
    id: 'thread-3',
    title: 'Cómo estructurar prompts para análisis de datos con ChatGPT',
    description: 'He estado experimentando con diferentes estructuras de prompts para analizar datos de ventas y quiero compartir lo que he aprendido. También me gustaría escuchar sus técnicas.',
    category: 'projects',
    tags: ['data-analysis', 'ai-assistants', 'real-case'],
    author: mockAuthors[1],
    projectId: 'proj-2',
    status: 'open',
    createdAt: '2025-12-18T16:45:00Z',
    lastActivity: '2025-12-21T11:30:00Z',
    responseCount: 8,
  },
  {
    id: 'thread-4',
    title: 'Busco colaborador para proyecto de chatbot de atención al cliente',
    description: 'Estoy desarrollando un chatbot para mi empresa y busco a alguien con experiencia en análisis de datos para ayudarme a mejorar las respuestas basándome en métricas. ¿Alguien interesado?',
    category: 'collaborations',
    tags: ['ai-assistants', 'data-analysis'],
    author: mockAuthors[2],
    status: 'open',
    createdAt: '2025-12-17T09:20:00Z',
    lastActivity: '2025-12-20T14:00:00Z',
    responseCount: 5,
  },
  {
    id: 'thread-5',
    title: 'Problema con la plataforma: No puedo subir entregables en PDF',
    description: 'Al intentar subir mi entregable del módulo 2 en formato PDF, la plataforma muestra un error genérico. Ya intenté con diferentes navegadores.',
    category: 'questions',
    tags: ['blocker'],
    author: mockAuthors[0],
    status: 'open',
    createdAt: '2025-12-16T11:00:00Z',
    lastActivity: '2025-12-16T15:30:00Z',
    responseCount: 3,
  },
  {
    id: 'thread-6',
    title: 'Propuesta: Agregar sección de casos de éxito en la plataforma',
    description: 'Sería genial tener un espacio donde los participantes que ya completaron la ruta puedan compartir sus casos de éxito y resultados. Esto motivaría a los nuevos participantes.',
    category: 'ideas',
    tags: ['real-case'],
    author: mockAuthors[2],
    status: 'open',
    createdAt: '2025-12-15T08:30:00Z',
    lastActivity: '2025-12-19T17:45:00Z',
    responseCount: 15,
  },
  {
    id: 'thread-7',
    title: 'Mejores prácticas para crear asistentes con Claude',
    description: 'Después de la sesión en vivo de la semana pasada, estuve experimentando con Claude para crear un asistente de recursos humanos. Comparto mis aprendizajes y algunas preguntas que me surgieron.',
    category: 'projects',
    tags: ['ai-assistants', 'workshop', 'real-case'],
    author: mockAuthors[1],
    workshopId: 'evt5',
    status: 'open',
    createdAt: '2025-12-14T13:15:00Z',
    lastActivity: '2025-12-21T08:00:00Z',
    responseCount: 7,
  },
  {
    id: 'thread-8',
    title: '¿Cómo manejar datos sensibles en automatizaciones?',
    description: 'Estoy creando una automatización que procesa información de clientes. ¿Cuáles son las mejores prácticas de seguridad que debería seguir? ¿Hay alguna consideración especial con Make o n8n?',
    category: 'questions',
    tags: ['automation', 'real-case'],
    author: mockAuthors[0],
    status: 'open',
    createdAt: '2025-12-13T10:00:00Z',
    lastActivity: '2025-12-18T12:20:00Z',
    responseCount: 6,
  },
];

export const mockResponses: ThreadResponse[] = [
  // Responses for thread-1
  {
    id: 'resp-1-1',
    threadId: 'thread-1',
    content: 'Hola María, tuve el mismo problema la semana pasada. El issue era que el token de Google Sheets había expirado. Intenta reconectar la cuenta de Google en Make.',
    author: mockAuthors[1],
    isSolution: false,
    createdAt: '2025-12-20T15:00:00Z',
  },
  {
    id: 'resp-1-2',
    threadId: 'thread-1',
    content: 'Además de lo que dice Carlos, verifica que no tengas límites de API excedidos. Google tiene un límite de solicitudes por minuto.',
    author: mockAuthors[2],
    isSolution: false,
    createdAt: '2025-12-20T16:30:00Z',
  },
  {
    id: 'resp-1-3',
    threadId: 'thread-1',
    content: 'María, el problema suele ser el timeout por defecto de Make. Ve a la configuración del módulo de Google Sheets y aumenta el timeout a 120 segundos. También asegúrate de que la hoja no tenga demasiadas fórmulas que se recalculen con cada cambio. ¡Esto me funcionó perfectamente!',
    author: mockAuthors[3],
    isSolution: true,
    createdAt: '2025-12-21T09:15:00Z',
  },
  {
    id: 'resp-1-4',
    threadId: 'thread-1',
    content: '¡Gracias Dr. Roberto! Eso era exactamente el problema. Aumenté el timeout y ahora funciona perfecto. 🎉',
    author: mockAuthors[0],
    isSolution: false,
    createdAt: '2025-12-21T09:45:00Z',
  },
  // Responses for thread-3
  {
    id: 'resp-3-1',
    threadId: 'thread-3',
    content: 'Excelente tema Carlos. Yo uso una estructura de 4 partes: Contexto → Datos → Instrucción → Formato esperado. Me ha funcionado muy bien para análisis de tendencias.',
    author: mockAuthors[2],
    isSolution: false,
    createdAt: '2025-12-19T08:00:00Z',
  },
  {
    id: 'resp-3-2',
    threadId: 'thread-3',
    content: 'Algo que me ha ayudado mucho es incluir ejemplos de output en el prompt. Así ChatGPT entiende exactamente qué formato quieres.',
    author: mockAuthors[0],
    isSolution: false,
    createdAt: '2025-12-19T10:30:00Z',
  },
  // Responses for thread-5
  {
    id: 'resp-5-1',
    threadId: 'thread-5',
    content: 'María, ¿podrías verificar que el PDF no exceda los 10MB? Es el límite actual de la plataforma.',
    author: mockAuthors[4],
    isSolution: false,
    createdAt: '2025-12-16T11:30:00Z',
  },
  {
    id: 'resp-5-2',
    threadId: 'thread-5',
    content: 'Encontré el problema. Había un issue con PDFs que tenían caracteres especiales en el nombre. Ya está corregido. Por favor intenta de nuevo con un nombre de archivo simple (sin acentos ni espacios).',
    author: mockAuthors[3],
    isSolution: true,
    createdAt: '2025-12-16T15:30:00Z',
  },
  {
    id: 'resp-5-3',
    threadId: 'thread-5',
    content: 'Confirmado, ya pude subir mi entregable. ¡Gracias!',
    author: mockAuthors[0],
    isSolution: false,
    createdAt: '2025-12-16T16:00:00Z',
  },
];

// Helper to get mock projects and workshops for filters
export const mockProjects = [
  { id: 'proj-1', name: 'Automatización de atención al cliente' },
  { id: 'proj-2', name: 'Dashboard de análisis de ventas' },
  { id: 'proj-3', name: 'Sistema de onboarding automatizado' },
];

export const mockWorkshops = [
  { id: 'evt5', name: 'Sesión en Vivo: Creando asistentes inteligentes' },
  { id: 'evt7', name: 'Sesión en Vivo: Automatizaciones con Make' },
  { id: 'evt8', name: 'Sesión en Vivo: Vibe Coding con Lovable' },
];
